import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const exportFormatSchema = z.enum(['csv', 'pdf', 'xlsx', 'qbo', 'iif']);
export const exportFileFormatSchema = z.enum(['csv', 'pdf', 'xlsx', 'qbo', 'iif', 'change_log', 'evidence_manifest', 'zip']);
export const exportFileSchema = z.object({
  format: exportFileFormatSchema,
  label: z.string(),
  status: z.enum(['queued', 'generating', 'done', 'failed']),
  job_id: z.string().nullable(),
  sha256: z.string().nullable(),
  byte_size: z.number().int().nonnegative().nullable(),
  error_code: z.string().nullable(),
}).strict();

export const exportVersionSchema = z.object({
  export_version_id: z.string(),
  version: z.number().int().positive(),
  revision: z.number().int().positive(),
  created_at: z.string(),
  by: z.string(),
  row_count: z.number().int().nonnegative(),
  unresolved: z.object({
    open_items: z.number().int().nonnegative(),
    uncategorized: z.number().int().nonnegative(),
    missing_receipts: z.number().int().nonnegative(),
    unverified_1099: z.number().int().nonnegative(),
  }).strict(),
  taxonomy_version: z.string(),
  snapshot_sha256: z.string().regex(/^[a-f0-9]{64}$/),
  filed_at: z.string().nullable(),
  files: z.array(exportFileSchema),
}).strict();

export const yearLockSchema = z.object({
  locked: z.boolean(),
  version: z.number().int().positive(),
  revision: z.number().int().positive(),
  state_version: z.number().int().positive(),
  filed_at: z.string().nullable(),
  by: z.string().nullable(),
}).strict();

export const accountantExportsSchema = z.object({
  versions: z.array(exportVersionSchema),
  year_lock: yearLockSchema,
}).strict();

export type AccountantExports = z.infer<typeof accountantExportsSchema>;
export type ExportVersion = z.infer<typeof exportVersionSchema>;
export type ExportFormat = z.infer<typeof exportFormatSchema>;

export function useAccountantExports(grantId: string | null | undefined, taxYear: number | null | undefined) {
  return useQuery({
    queryKey: ['accountant-exports', grantId, taxYear],
    enabled: Boolean(grantId && taxYear != null),
    queryFn: async (): Promise<AccountantExports> => {
      const response = await client.get(`/api/accountant/clients/${grantId}/years/${taxYear}/exports`);
      return parseEnvelope(accountantExportsSchema, response.data).data;
    },
  });
}

export type CreateAccountantExportInput = {
  grantId: string;
  taxYear: number;
  formats: ExportFormat[];
  expectedStateVersion: number;
  idempotencyKey: string;
};

export function useCreateAccountantExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAccountantExportInput) => {
      const response = await client.post(`/api/accountant/clients/${input.grantId}/years/${input.taxYear}/exports`, {
        formats: input.formats,
        expected_state_version: input.expectedStateVersion,
        idempotency_key: input.idempotencyKey,
      });
      return response.data;
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['accountant-exports', input.grantId, input.taxYear] });
    },
  });
}

export type MarkFiledInput = {
  grantId: string;
  taxYear: number;
  exportVersionId: string;
  expectedStateVersion: number;
  acknowledgeUnresolved: boolean;
};

export function useMarkAccountantYearFiled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkFiledInput) => {
      const response = await client.post(`/api/accountant/clients/${input.grantId}/years/${input.taxYear}/mark-filed`, {
        export_version_id: input.exportVersionId,
        expected_state_version: input.expectedStateVersion,
        acknowledge_unresolved: input.acknowledgeUnresolved,
      });
      return response.data;
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['accountant-exports', input.grantId, input.taxYear] });
    },
  });
}

export type ReopenInput = { grantId: string; taxYear: number; expectedStateVersion: number; reason: string };

export function useReopenAccountantYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReopenInput) => {
      const response = await client.post(`/api/accountant/clients/${input.grantId}/years/${input.taxYear}/reopen`, {
        expected_state_version: input.expectedStateVersion,
        reason: input.reason,
      });
      return response.data;
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['accountant-exports', input.grantId, input.taxYear] });
    },
  });
}
